import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../context/AuthContext';
import { INTERNAL_KYC_UI } from '../../lib/featureFlags';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Shield,
  Briefcase, TrendingUp, DollarSign, FileText,
  CheckCircle, AlertTriangle, ExternalLink, History,
  PieChart, Edit2, Save, X, Users, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================================
// CONFIG - Shared with Party Management
// ============================================================================

const partyTypeConfig = {
  investor_individual: { label: 'Individual Investor', color: 'bg-blue-100 text-blue-700' },
  investor_institutional: { label: 'Institutional Investor', color: 'bg-purple-100 text-purple-700' },
  developer: { label: 'Developer', color: 'bg-green-100 text-green-700' },
  sponsor: { label: 'Sponsor', color: 'bg-emerald-100 text-emerald-700' },
  issuer: { label: 'Issuer', color: 'bg-indigo-100 text-indigo-700' },
  property_owner: { label: 'Property Owner', color: 'bg-amber-100 text-amber-700' },
  service_provider: { label: 'Service Provider', color: 'bg-gray-100 text-gray-700' },
  platform: { label: 'Platform', color: 'bg-pink-100 text-pink-700' },
};

const statusConfig = {
  pending_verification: { label: 'Pending Verification', color: 'bg-yellow-100 text-yellow-700' },
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-700' },
};

const kycLevelConfig = {
  none: { label: 'None', color: 'bg-gray-100 text-gray-600' },
  basic: { label: 'Basic', color: 'bg-blue-100 text-blue-700' },
  standard: { label: 'Standard', color: 'bg-green-100 text-green-700' },
  enhanced: { label: 'Enhanced', color: 'bg-purple-100 text-purple-700' },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700' },
};

const accreditationConfig = {
  not_applicable: { label: 'N/A', color: 'bg-gray-100 text-gray-600' },
  retail: { label: 'Retail', color: 'bg-blue-100 text-blue-700' },
  sophisticated: { label: 'Sophisticated', color: 'bg-green-100 text-green-700' },
  professional: { label: 'Professional', color: 'bg-purple-100 text-purple-700' },
  institutional: { label: 'Institutional', color: 'bg-indigo-100 text-indigo-700' },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminInvestorProfilePage() {
  const { investorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [investor, setInvestor] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Edit mode states
  const [editingContact, setEditingContact] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showKycDialog, setShowKycDialog] = useState(false);
  
  // Edit form data
  const [contactForm, setContactForm] = useState({
    display_name: '',
    legal_name: '',
    email: '',
    phone: '',
    tax_id: '',
    country_code: '',
  });
  
  const [statusForm, setStatusForm] = useState({
    status: '',
    reason: '',
  });
  
  const [kycForm, setKycForm] = useState({
    kyc_level: '',
    accreditation_status: '',
  });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const [deletingEventId, setDeletingEventId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch investor details from party management endpoint
      const investorRes = await api.get(`/admin/v2/parties/${investorId}`);
      const investorData = investorRes.data;
      setInvestor(investorData);
      
      // Initialize contact form with current data
      setContactForm({
        display_name: investorData.display_name || '',
        legal_name: investorData.legal_name || '',
        email: investorData.contact?.email || investorData.email || '',
        phone: investorData.contact?.phone || investorData.phone || '',
        tax_id: investorData.tax_id || '',
        country_code: investorData.tax_residency_country || investorData.country_code || 'PT',
      });
      
      setKycForm({
        kyc_level: investorData.kyc_level || 'none',
        accreditation_status: investorData.accreditation_status || 'not_applicable',
      });
      
      // Fetch all instruments to find holdings
      const instrumentsRes = await api.get('/admin/projects/instruments/all');
      const instruments = instrumentsRes.data.instruments || [];
      
      // Fetch holdings across all instruments
      const holdingsData = [];
      const allEvents = [];
      
      for (const instrument of instruments) {
        try {
          const capTableRes = await api.get(`/admin/v2/captable/securities/${instrument.id}/captable`);
          const positions = capTableRes.data?.positions || [];
          
          const position = positions.find(p => p.investor_id === investorId);
          if (position && position.units_held > 0) {
            holdingsData.push({
              instrument_id: instrument.id,
              instrument_code: instrument.code,
              instrument_name: instrument.name,
              security_class: instrument.security_class,
              project_id: instrument.project_id,
              units_held: position.units_held,
              percent_of_outstanding: position.percent_of_outstanding,
              unit_price: instrument.unit_price_at_issuance,
              currency: instrument.currency,
              total_value: (position.units_held * parseFloat(instrument.unit_price_at_issuance || 1)).toFixed(2),
            });
          }
          
          const eventsRes = await api.get(`/admin/v2/captable/securities/${instrument.id}/events`);
          const instrumentEvents = (eventsRes.data?.events || [])
            .filter(e => e.investor_id === investorId || e.counterparty_id === investorId)
            .map(e => ({
              ...e,
              instrument_code: instrument.code,
              instrument_name: instrument.name,
            }));
          allEvents.push(...instrumentEvents);
        } catch (err) {
          // Silently skip instruments without cap tables
        }
      }
      
      setHoldings(holdingsData);
      setEvents(allEvents.sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date)));
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load investor profile');
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteEvent = async (event) => {
    const label = event.event_type === 'transfer' ? 'transfer' : 'mint';
    const confirmed = window.confirm(
      `Delete this ${label} of ${event.units_delta?.toLocaleString()} units?\n\n` +
      `The units and the investment registered against them are put back as they were. ` +
      `This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingEventId(event.id);
    try {
      const res = await api.delete(`/admin/v2/captable/events/${event.id}`);
      toast.success(res.data?.message || `${label} deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${label}`);
    } finally {
      setDeletingEventId(null);
    }
  };

  // ============================================================================
  // UPDATE HANDLERS
  // ============================================================================

  const handleSaveContact = async () => {
    if (!contactForm.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }
    if (!contactForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/v2/parties/${investorId}`, {
        display_name: contactForm.display_name,
        legal_name: contactForm.legal_name || contactForm.display_name,
        contact: {
          email: contactForm.email,
          phone: contactForm.phone || null,
        },
        tax_residency_country: contactForm.country_code,
        reason: 'Contact information updated by admin',
      });
      
      toast.success('Contact information updated');
      setEditingContact(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update contact info');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusForm.status) {
      toast.error('Please select a status');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/v2/parties/${investorId}/status`, {
        status: statusForm.status,
        reason: statusForm.reason || 'Status updated by admin',
      });
      
      toast.success('Status updated');
      setShowStatusDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateKyc = async () => {
    setSaving(true);
    try {
      // Update KYC level
      await api.put(`/admin/v2/parties/${investorId}/kyc`, {
        kyc_level: kycForm.kyc_level,
      });
      
      // Update accreditation via general update
      await api.put(`/admin/v2/parties/${investorId}`, {
        accreditation_status: kycForm.accreditation_status,
      });
      
      toast.success('KYC and accreditation updated');
      setShowKycDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update KYC');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelContactEdit = () => {
    setContactForm({
      display_name: investor?.display_name || '',
      legal_name: investor?.legal_name || '',
      email: investor?.email || '',
      phone: investor?.phone || '',
      tax_id: investor?.tax_id || '',
      country_code: investor?.country_code || 'PT',
    });
    setEditingContact(false);
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats
  const stats = {
    totalHoldings: holdings.length,
    totalUnits: holdings.reduce((sum, h) => sum + (h.units_held || 0), 0),
    totalValue: holdings.reduce((sum, h) => sum + parseFloat(h.total_value || 0), 0),
    totalEvents: events.length,
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="min-h-screen bg-background">
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Investor Not Found</h2>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const partyType = partyTypeConfig[investor.party_type] || { label: investor.party_type, color: 'bg-gray-100 text-gray-700' };
  const status = statusConfig[investor.status] || { label: investor.status, color: 'bg-gray-100 text-gray-700' };
  const kycLevel = kycLevelConfig[investor.kyc_level] || { label: investor.kyc_level || 'None', color: 'bg-gray-100 text-gray-600' };
  const accreditation = accreditationConfig[investor.accreditation_status] || { label: 'N/A', color: 'bg-gray-100 text-gray-600' };

  return (
    <div className="min-h-screen bg-background">
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button & Actions */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/v2/parties')}>
            <Users className="h-4 w-4 mr-2" />
            View All Parties
          </Button>
        </div>

        {/* Profile Header */}
        <div className="flex items-start gap-6 mb-8">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
            <User className="h-10 w-10 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl font-bold">{investor.display_name}</h1>
              <Badge className={kycLevel.color}>{kycLevel.label}</Badge>
              <Badge className={status.color}>{status.label}</Badge>
            </div>
            <p className="text-foreground-muted">{investor.legal_name || investor.display_name}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-foreground-muted flex-wrap">
              <Badge variant="outline" className={partyType.color}>{partyType.label}</Badge>
              <span className="font-mono">{investor.code}</span>
              {investor.created_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Since {formatDate(investor.created_at)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Holdings</p>
                <p className="text-xl font-bold">{stats.totalHoldings}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <PieChart className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Total Units</p>
                <p className="text-xl font-bold">{stats.totalUnits.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Total Value</p>
                <p className="text-xl font-bold">€{stats.totalValue.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <History className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Transactions</p>
                <p className="text-xl font-bold">{stats.totalEvents}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Contact & Status */}
          <div className="space-y-6">
            {/* Contact Info - Editable */}
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Contact Information</h3>
                {!editingContact ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditingContact(true)} data-testid="edit-contact-btn">
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={handleCancelContactEdit}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={handleSaveContact} disabled={saving} data-testid="save-contact-btn">
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                  </div>
                )}
              </div>
              
              {editingContact ? (
                <div className="space-y-4">
                  <div>
                    <Label>Display Name *</Label>
                    <Input 
                      value={contactForm.display_name}
                      onChange={(e) => setContactForm({ ...contactForm, display_name: e.target.value })}
                      className="mt-1"
                      data-testid="edit-display-name"
                    />
                  </div>
                  <div>
                    <Label>Legal Name</Label>
                    <Input 
                      value={contactForm.legal_name}
                      onChange={(e) => setContactForm({ ...contactForm, legal_name: e.target.value })}
                      className="mt-1"
                      data-testid="edit-legal-name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="mt-1"
                      data-testid="edit-email"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="mt-1"
                      data-testid="edit-phone"
                    />
                  </div>
                  <div>
                    <Label>Tax ID</Label>
                    <Input 
                      value={contactForm.tax_id}
                      onChange={(e) => setContactForm({ ...contactForm, tax_id: e.target.value })}
                      className="mt-1"
                      data-testid="edit-tax-id"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Select value={contactForm.country_code} onValueChange={(v) => setContactForm({ ...contactForm, country_code: v })}>
                      <SelectTrigger className="mt-1" data-testid="edit-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PT">Portugal</SelectItem>
                        <SelectItem value="ES">Spain</SelectItem>
                        <SelectItem value="FR">France</SelectItem>
                        <SelectItem value="DE">Germany</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(investor.contact?.email || investor.email) && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-foreground-muted" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-muted">Email</p>
                        <p className="text-sm font-medium">{investor.contact?.email || investor.email}</p>
                      </div>
                    </div>
                  )}
                  {(investor.contact?.phone || investor.phone) && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Phone className="h-4 w-4 text-foreground-muted" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-muted">Phone</p>
                        <p className="text-sm font-medium">{investor.contact?.phone || investor.phone}</p>
                      </div>
                    </div>
                  )}
                  {(investor.tax_residency_country || investor.country_code) && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <MapPin className="h-4 w-4 text-foreground-muted" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-muted">Country</p>
                        <p className="text-sm font-medium">{investor.tax_residency_country || investor.country_code}</p>
                      </div>
                    </div>
                  )}
                  {investor.tax_id && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FileText className="h-4 w-4 text-foreground-muted" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-muted">Tax ID</p>
                        <p className="text-sm font-medium">{investor.tax_id}</p>
                      </div>
                    </div>
                  )}
                  {!investor.contact?.email && !investor.email && !investor.contact?.phone && !investor.phone && !investor.tax_residency_country && !investor.country_code && !investor.tax_id && (
                    <p className="text-sm text-foreground-muted">No contact information available</p>
                  )}
                </div>
              )}
            </Card>

            {/* Verification Status - Editable */}
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Verification Status</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-muted">Account Status</span>
                  <div className="flex items-center gap-2">
                    <Badge className={status.color}>{status.label}</Badge>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                      setStatusForm({ status: investor.status, reason: '' });
                      setShowStatusDialog(true);
                    }} data-testid="edit-status-btn">
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-muted">KYC Level</span>
                  <div className="flex items-center gap-2">
                    <Badge className={kycLevel.color}>{kycLevel.label}</Badge>
                    {/* Verification is the external provider's call now; this stays read-only. */}
                    {INTERNAL_KYC_UI && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                        setKycForm({
                          kyc_level: investor.kyc_level || 'none',
                          accreditation_status: investor.accreditation_status || 'not_applicable',
                        });
                        setShowKycDialog(true);
                      }} data-testid="edit-kyc-btn">
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-muted">Accreditation</span>
                  <Badge className={accreditation.color}>{accreditation.label}</Badge>
                </div>
              </div>
            </Card>
          </div>

          {/* Middle & Right - Holdings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Holdings */}
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Investment Holdings
              </h3>
              
              {holdings.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                  <p className="text-foreground-muted text-sm">No holdings yet</p>
                  <p className="text-foreground-muted text-xs mt-1">This investor has no positions in any instruments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {holdings.map((holding, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/captable/${holding.instrument_id}`)}
                      data-testid={`holding-${holding.instrument_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Briefcase className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-foreground-muted">{holding.instrument_code}</span>
                            <Badge variant="outline" className="text-xs capitalize">{holding.security_class}</Badge>
                          </div>
                          <h4 className="font-medium">{holding.instrument_name}</h4>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{holding.units_held?.toLocaleString()} units</p>
                        <p className="text-sm text-foreground-muted">
                          {holding.currency} {holding.total_value} • {holding.percent_of_outstanding}%
                        </p>
                      </div>
                      <ExternalLink className="h-4 w-4 text-foreground-muted ml-2" />
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Transaction History */}
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <History className="h-4 w-4" />
                Transaction History
              </h3>
              
              {events.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                  <p className="text-foreground-muted text-sm">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {events.slice(0, 20).map((event) => (
                    <div 
                      key={event.id}
                      className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                      data-testid={`investor-event-${event.id}`}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                        event.event_type === 'mint' ? 'bg-green-100' :
                        event.event_type === 'transfer' ? 'bg-blue-100' :
                        event.event_type === 'redemption' ? 'bg-amber-100' :
                        'bg-gray-100'
                      )}>
                        {event.event_type === 'mint' ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : event.event_type === 'transfer' ? (
                          <ArrowLeft className="h-4 w-4 text-blue-600" />
                        ) : (
                          <FileText className="h-4 w-4 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">{event.event_type}</Badge>
                          <span className="font-mono text-xs text-foreground-muted">{event.instrument_code}</span>
                          <span className="text-xs text-foreground-muted">
                            {formatDate(event.effective_date)}
                          </span>
                        </div>
                        <p className="text-sm mt-1">
                          {event.event_type === 'mint' ? '+' : ''}{event.units_delta?.toLocaleString()} units
                          {event.event_type === 'transfer' && event.investor_id === investorId && ' (sent)'}
                          {event.event_type === 'transfer' && event.counterparty_id === investorId && ' (received)'}
                        </p>
                        {event.reason && (
                          <p className="text-xs text-foreground-muted mt-1 truncate">{event.reason}</p>
                        )}
                      </div>
                      {['mint', 'transfer'].includes(event.event_type) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 flex-shrink-0 text-foreground-muted hover:text-red-600"
                          disabled={deletingEventId === event.id}
                          onClick={() => handleDeleteEvent(event)}
                          title={`Delete this ${event.event_type}`}
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* ================================================================== */}
      {/* STATUS UPDATE DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update Account Status</DialogTitle>
            <DialogDescription>
              Change the account status for this investor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger className="mt-1" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_verification">Pending Verification</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input 
                value={statusForm.reason}
                onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
                placeholder="Reason for status change"
                className="mt-1"
                data-testid="status-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={saving} data-testid="confirm-status-btn">
              {saving ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* KYC UPDATE DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showKycDialog} onOpenChange={setShowKycDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update KYC & Accreditation</DialogTitle>
            <DialogDescription>
              Update the verification level for this investor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>KYC Level</Label>
              <Select value={kycForm.kyc_level} onValueChange={(v) => setKycForm({ ...kycForm, kyc_level: v })}>
                <SelectTrigger className="mt-1" data-testid="kyc-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="enhanced">Enhanced</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Accreditation Status</Label>
              <Select value={kycForm.accreditation_status} onValueChange={(v) => setKycForm({ ...kycForm, accreditation_status: v })}>
                <SelectTrigger className="mt-1" data-testid="accreditation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="sophisticated">Sophisticated</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKycDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateKyc} disabled={saving} data-testid="confirm-kyc-btn">
              {saving ? 'Updating...' : 'Update KYC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
